/**
 * Blur a VSelect when the user clicks away from it — restoring Vuetify 2's
 * behaviour.
 *
 * V3's VSelect hands focus back to its activator input whenever the menu closes,
 * including when the close was caused by a click OUTSIDE the control. V2 simply
 * blurred. The difference is not only cosmetic: because the select keeps focus,
 * the next Tab moves to the FOLLOWING field rather than back into the select the
 * user just left — noticeable on the list pages' top bars, where three selects
 * sit side by side.
 *
 * Reproduce (identical steps, differing results):
 *   open a top-bar select's menu, then click empty page space without picking an
 *   item.  V2 -> document.body has focus, Tab returns to that select.
 *   V3 -> the select's input has focus again, Tab skips to the next select.
 *
 * Done here rather than per component because it is a framework-behaviour
 * difference affecting every VSelect in the app (~20 on the top bars alone), and
 * Vuetify's global `defaults` can only set props, not listeners.
 *
 * TIMING MATTERS. While the menu is open, focus lives in the teleported overlay,
 * not in the select — and Vuetify moves it back to the activator as part of
 * closing, which happens AFTER the click event and after the next animation
 * frame. So this cannot decide anything at pointerdown time, and a single
 * deferred check is racy: it runs while focus is still in the overlay and sees
 * nothing to do. Instead, an outside click opens a short window during which any
 * focus landing inside a select is taken back.
 *
 * Scoped deliberately: only `.v-select` (text fields already blur on an outside
 * click, since nothing re-focuses them), and only when the click landed outside
 * both every select and any open overlay — so choosing an item, or clicking the
 * select's own icon, is untouched.
 */
export default defineNuxtPlugin(() => {
  if (typeof document === 'undefined') {
    return
  }

  // How long after an outside click to keep watching for Vuetify handing focus
  // back. Comfortably longer than the menu-close transition, short enough that
  // it cannot swallow a genuine later focus.
  const WATCH_MS = 300
  let watchUntil = 0
  let lastOutsideTarget: Element | null = null

  const isInsideSelectOrOverlay = (el: Element | null) =>
    !!el?.closest?.('.v-select') ||
    !!el?.closest?.('.v-overlay__content, .v-menu__content')

  document.addEventListener(
    'pointerdown',
    (event) => {
      const target = event.target as Element | null
      if (!target || typeof target.closest !== 'function') {
        return
      }
      if (isInsideSelectOrOverlay(target)) {
        return
      }
      watchUntil = Date.now() + WATCH_MS
      lastOutsideTarget = target
      // Cover the case where focus is already on the select's input (menu shut).
      queueMicrotask(() => blurIfSelectFocused())
    },
    true
  )

  function blurIfSelectFocused() {
    const active = document.activeElement as HTMLElement | null
    if (
      active &&
      active.closest?.('.v-select') &&
      typeof active.blur === 'function'
    ) {
      active.blur()
    }
  }

  // Vuetify restores focus to the activator when the menu finishes closing; if
  // that happens inside the window opened by an outside click, undo it.
  document.addEventListener(
    'focusin',
    (event) => {
      if (Date.now() > watchUntil) {
        return
      }
      const target = event.target as Element | null
      if (!target?.closest?.('.v-select')) {
        return
      }
      ;(target as HTMLElement).blur?.()

      // Blurring alone leaves the browser's sequential-focus starting point on
      // the select, so the next Tab jumps to the FOLLOWING control instead of
      // back into the one just left. A real click on a non-focusable element
      // moves that starting point to the clicked element; reproduce it by
      // focusing the click target briefly (tabindex -1 makes it programmatically
      // focusable without entering the tab order) and releasing it again, which
      // leaves document.body active exactly as V2 did.
      const clicked = lastOutsideTarget as HTMLElement | null
      if (
        clicked &&
        document.contains(clicked) &&
        typeof clicked.focus === 'function'
      ) {
        const hadTabIndex = clicked.hasAttribute('tabindex')
        if (!hadTabIndex) {
          clicked.setAttribute('tabindex', '-1')
        }
        clicked.focus({ preventScroll: true })
        clicked.blur()
        if (!hadTabIndex) {
          clicked.removeAttribute('tabindex')
        }
      }
    },
    true
  )
})
